import { ref, watch, onBeforeUnmount } from 'vue';

// Pointer travel (in px) after which a press is treated as a drag instead of a click.
export const DRAG_THRESHOLD = 5;
// Gap kept between the launcher and the viewport edges.
export const EDGE_MARGIN = 16;

/**
 * Checks whether the pointer moved far enough to be considered a drag.
 * @param {number} dx - Horizontal distance travelled.
 * @param {number} dy - Vertical distance travelled.
 * @param {number} [threshold] - Distance in px that separates a click from a drag.
 * @returns {boolean} True when the movement should be treated as a drag.
 */
export const exceedsDragThreshold = (dx, dy, threshold = DRAG_THRESHOLD) =>
  Math.sqrt(dx * dx + dy * dy) > threshold;

/**
 * Keeps a position inside the viewport, honouring the edge margin.
 * @param {{x: number, y: number}} position - Desired top-left position.
 * @param {Object} bounds - Element and viewport metrics.
 * @param {number} bounds.width - Width of the draggable element.
 * @param {number} bounds.height - Height of the draggable element.
 * @param {number} bounds.viewportWidth - Viewport width.
 * @param {number} bounds.viewportHeight - Viewport height.
 * @param {number} [bounds.margin] - Gap kept from the viewport edges.
 * @returns {{x: number, y: number}} Clamped position.
 */
export const clampToViewport = (
  { x, y },
  { width, height, viewportWidth, viewportHeight, margin = EDGE_MARGIN }
) => {
  const maxX = Math.max(margin, viewportWidth - width - margin);
  const maxY = Math.max(margin, viewportHeight - height - margin);
  return {
    x: Math.min(Math.max(x, margin), maxX),
    y: Math.min(Math.max(y, margin), maxY),
  };
};

/**
 * Snaps a position to the closest vertical viewport edge, keeping the y axis free.
 * @param {{x: number, y: number}} position - Position at the end of the drag.
 * @param {Object} bounds - Element and viewport metrics, see clampToViewport.
 * @returns {{x: number, y: number}} Position snapped to the nearest edge.
 */
export const snapToNearestEdge = (position, bounds) => {
  const { width, viewportWidth, margin = EDGE_MARGIN } = bounds;
  const isNearerToLeft = position.x + width / 2 < viewportWidth / 2;
  const snappedX = isNearerToLeft ? margin : viewportWidth - width - margin;
  return clampToViewport({ x: snappedX, y: position.y }, bounds);
};

/**
 * Makes an element draggable with pointer events, clamped to the viewport and
 * snapped to the nearest horizontal edge on release.
 * @param {Object} options - Composable options.
 * @param {import('vue').Ref<HTMLElement|null>} options.elementRef - The draggable element.
 * @param {import('vue').Ref<{x: number, y: number}|null>} options.initialPosition - Persisted position, if any.
 * @param {Function} [options.onDragEnd] - Called with the final position after a drag.
 * @returns {Object} Reactive drag state and the pointerdown handler.
 */
export function useDraggableLauncher({
  elementRef,
  initialPosition,
  onDragEnd,
}) {
  const position = ref(null);
  const isDragging = ref(false);
  // Stays true between a completed drag and the next pointerdown so the
  // trailing click event can be ignored.
  const hasDragged = ref(false);

  let dragOrigin = null;
  let dragListeners = null;

  const getBounds = () => {
    const element = elementRef.value;
    return {
      width: element.offsetWidth,
      height: element.offsetHeight,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    };
  };

  const handlePointerMove = event => {
    const dx = event.clientX - dragOrigin.pointerX;
    const dy = event.clientY - dragOrigin.pointerY;
    if (!isDragging.value && !exceedsDragThreshold(dx, dy)) return;

    isDragging.value = true;
    hasDragged.value = true;
    position.value = clampToViewport(
      { x: dragOrigin.x + dx, y: dragOrigin.y + dy },
      getBounds()
    );
  };

  const stopListening = () => {
    dragListeners?.abort();
    dragListeners = null;
  };

  const handlePointerUp = () => {
    stopListening();
    if (!isDragging.value) return;

    isDragging.value = false;
    position.value = snapToNearestEdge(position.value, getBounds());
    onDragEnd?.(position.value);
  };

  const onPointerDown = event => {
    if (event.button) return;

    const rect = elementRef.value.getBoundingClientRect();
    dragOrigin = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      x: rect.left,
      y: rect.top,
    };
    hasDragged.value = false;

    stopListening();
    dragListeners = new AbortController();
    const { signal } = dragListeners;
    window.addEventListener('pointermove', handlePointerMove, { signal });
    window.addEventListener('pointerup', handlePointerUp, { signal });
    window.addEventListener('pointercancel', handlePointerUp, { signal });
  };

  const applyPosition = value => {
    if (!elementRef.value || !value) return;
    position.value = clampToViewport(value, getBounds());
  };

  const handleResize = () => applyPosition(position.value);

  watch(
    [elementRef, initialPosition],
    ([element]) => {
      if (!element || isDragging.value) return;
      applyPosition(initialPosition.value);
    },
    { immediate: true, flush: 'post' }
  );

  window.addEventListener('resize', handleResize);
  onBeforeUnmount(() => {
    stopListening();
    window.removeEventListener('resize', handleResize);
  });

  return { position, isDragging, hasDragged, onPointerDown };
}
